import { STARTER_WORN, lookOf, newWireLook, wireLookOf, type Appearance, type DirectionName, type LookLayout, type LookLayoutCell, type LookManifest, type Looks } from "odyssey-shared";
import { composeSheets, newComposePlan } from "../looks/look-plan.ts";
import { corsFetchPath } from "../cors-address.ts";

/**
 * What the Look panel's preview draws (ADR-094 decision 27), as pure functions: the look standing, in one of four
 * facings, from the published piece sheets. A look is planned exactly as the game composes it (`composeSheets`, the
 * layout's drawing order, the slot's fallback for a piece the pointer lacks, no layer for a bare piece), with the
 * starter tools every character wears, so the preview is the character the world will show. Only the sheets of the
 * pieces shown are named, so only they are fetched.
 */

/**
 * The strip the preview stands in: the game's standing strip, weapon away.
 */
export const PREVIEW_STRIP = "idle";

/**
 * The facings in the order the design's turn row walks them: front, left, back, right.
 */
export const PREVIEW_FACINGS = ["down", "left", "up", "right"] as const satisfies readonly DirectionName[];

/**
 * The most art pixels are magnified, in logical pixels to one: the design's figure scale.
 */
export const PREVIEW_MAX_ZOOM = 7;

/**
 * The box every standing cell is drawn in, about the pivot: `left` and `top` are where its corner sits relative to
 * the point under the character's feet (negative is left of it and above it), so the figure does not move as it
 * turns.
 */
export interface PreviewStage {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * One frame of the preview: the sheets to draw, bottom first, each by its id and the path it is fetched at under the
 * asset base (`corsFetchPath`); the cell every one of them is read from; and the stage.
 */
export interface PreviewPlan {
  readonly facing: DirectionName;
  readonly sheets: readonly { readonly id: string; readonly path: string }[];
  readonly cell: LookLayoutCell;
  readonly stage: PreviewStage;
}

/**
 * The facing a turn leads to.
 *
 * @param facing The facing now.
 * @param by Steps along the turn row's order: 1 for Turn left (front to left), -1 for Turn right.
 * @returns The facing, wrapping at both ends.
 * @example
 * turnFacing("down", 1); // "left"
 * turnFacing("down", -1); // "right"
 */
export function turnFacing(facing: DirectionName, by: number): DirectionName {
  const count = PREVIEW_FACINGS.length;
  const from = Math.max(0, PREVIEW_FACINGS.indexOf(facing as (typeof PREVIEW_FACINGS)[number]));
  return PREVIEW_FACINGS[(((from + by) % count) + count) % count] as DirectionName;
}

/**
 * The standing cell of a facing.
 *
 * @param layout The look layout.
 * @param facing The facing.
 * @returns The cell, or null when the layout has no standing strip, no frame for the facing or no such cell.
 */
function standingCell(layout: LookLayout, facing: DirectionName): LookLayoutCell | null {
  const index = layout.strips[PREVIEW_STRIP]?.frames[facing]?.[0];
  return index === undefined ? null : (layout.cells[index] ?? null);
}

/**
 * The stage of a layout: the smallest box about the pivot that holds the standing cell of every facing.
 *
 * @param layout The look layout.
 * @returns The stage, or null when the layout has no standing cell at all.
 * @example
 * previewStage(layout); // { left: -9, top: -32, width: 19, height: 34 }
 */
export function previewStage(layout: LookLayout): PreviewStage | null {
  let left = Number.POSITIVE_INFINITY;
  let top = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  for (const facing of PREVIEW_FACINGS) {
    const cell = standingCell(layout, facing);
    if (cell === null) {
      continue;
    }
    left = Math.min(left, cell.offsetX);
    top = Math.min(top, cell.offsetY);
    right = Math.max(right, cell.offsetX + cell.width);
    bottom = Math.max(bottom, cell.offsetY + cell.height);
  }
  return left === Number.POSITIVE_INFINITY ? null : { left, top, width: right - left, height: bottom - top };
}

/**
 * Plans the preview of a look.
 *
 * @param looks The page's looks table.
 * @param manifest The look sheets' pointer the page loaded.
 * @param appearance The look's three keys.
 * @param facing The facing.
 * @returns The plan, or null when the layout cannot show a standing figure in that facing.
 */
export function previewPlan(looks: Looks, manifest: LookManifest, appearance: Appearance, facing: DirectionName): PreviewPlan | null {
  const cell = standingCell(manifest.layout, facing);
  const stage = previewStage(manifest.layout);
  if (cell === null || stage === null) {
    return null;
  }
  const look = newWireLook();
  wireLookOf(looks, lookOf(appearance, STARTER_WORN), look);
  const composed = composeSheets(looks, manifest.layout.compose, manifest.sheets, look, newComposePlan());
  return { facing, sheets: composed.sheets.map((id) => ({ id, path: corsFetchPath(manifest.sheets[id] as string) })), cell, stage };
}

/**
 * The zoom a stage is drawn at in a room: the largest whole number of logical pixels to one of art that fits, never
 * less than 1 and never more than `most`.
 *
 * @param room The room for the figure, in logical pixels.
 * @param stage The stage.
 * @param most The most it is magnified.
 * @returns The zoom.
 * @example
 * previewZoom({ width: 190, height: 240 }, { left: -9, top: -32, width: 19, height: 34 }); // 7
 */
export function previewZoom(room: { readonly width: number; readonly height: number }, stage: PreviewStage, most: number = PREVIEW_MAX_ZOOM): number {
  return Math.max(1, Math.min(most, Math.floor(Math.min(room.width / stage.width, room.height / stage.height))));
}
